/**
 * Athena Unified Theme — dual light/dark palettes
 *
 * Single source of truth for ALL colors across Pallas (chat, responses,
 * triage, standup, vulns). Two full palettes are defined below; `THEME` is a
 * MUTABLE singleton that `applyTheme(mode)` re-skins in place.
 *
 * HARD RULES (breakage otherwise):
 *   1. Components read `THEME.*` AT RENDER TIME only. Never snapshot THEME
 *      values into module-scope constants — index.js mutates THEME before
 *      first render (saved theme + the Ageleia embed re-skin), and the theme
 *      toggle re-applies a palette and reloads.
 *   2. Every color token stays a 6-DIGIT HEX (no rgb()/named colors/3-digit)
 *      unless listed under "alpha tokens" — the `${THEME.primary}40`
 *      alpha-suffix idiom across the app depends on it.
 *   3. New tokens must be added to BOTH palettes.
 *
 * Theme selection order (index.js): ?theme=ageleia → forced dark + ageleia
 * skin · localStorage['pallas-theme'] · default 'dark'.
 *
 * Import as: import { THEME, applyTheme, GRADIENTS, SHADOWS, GLOWS, TIMINGS } from '../utils/theme';
 */

export const THEME_STORAGE_KEY = 'pallas-theme';

// ─── Dark palette (default) ──────────────────────────────────────────────────

const DARK_THEME = {
  // ───── Backgrounds ─────
  bg:           '#151519',   // Page / modal panel background
  bgDeep:       '#0E0E11',   // Canvas / deepest layer
  card:         '#121216',   // Card / panel surface — aligned to the sidebar
  cardDark:     '#151519',   // Same as bg — flat sections / tab containers
  cardSubtle:   '#121216',   // Same as card — meta bars, table headers
  cardHover:    '#1A1A20',   // Hover state — slightly lighter than card
  railBg:       '#121216',   // Sidebar surface (darker than content in dark)

  // Alert-row surfaces (alpha tokens). Translucent overlays by default; the
  // Ageleia embed overrides these to solid charcoal via index.js.
  rowBg:        'rgba(255,255,255,0.02)',
  rowBgHover:   'rgba(255,255,255,0.04)',
  rowBgMuted:   'rgba(255,255,255,0.01)',

  // ───── Borders ─────
  border:       '#2A2B33',   // Standard border / divider
  borderLight:  '#35363F',   // Active / lighter border
  borderSubtle: '#1D1E24',   // Subtle row dividers
  borderFaint:  '#151519',   // Faintest divider

  // ───── Text ─────
  text:         '#ffffff',   // Primary text
  textBright:   '#e8f0ff',   // Bright emphasis
  textSecondary:'#8e9fbc',   // Secondary / labels
  textMuted:    '#5a6d8a',   // Muted (metadata, timestamps)
  textFaint:    '#3d5070',   // Faint (disabled-ish)
  textDisabled: '#2a3a54',   // Disabled

  // ───── Brand Accent Colors ─────
  primary:      '#3b82f6',   // Primary blue
  primaryDark:  '#2563eb',
  primaryLight: '#60a5fa',

  accent:       '#e2e8f0',   // Clean white-silver accent
  accentDark:   '#cbd5e1',
  accentContrast: '#0A1A1A', // Text/icon ON the accent color

  success:      '#10b981',
  successDark:  '#059669',
  successLight: '#7de2d1',
  successSoft:  '#34d399',   // Soft green (ok glyphs, live badges)

  danger:       '#ef4444',
  dangerDark:   '#dc2626',
  dangerSoft:   '#f87171',   // Soft red (cancel hovers, offline pills)

  warning:      '#f59e0b',
  warningDark:  '#d97706',
  warningYellow:'#fec514',
  warningSoft:  '#ff9f45',   // Soft amber (partial-result, overrun)

  info:         '#06b6d4',   // Cyan (triage actions, lifecycle)
  violet:       '#a78bfa',   // Violet (standup, AI-routed, GuardDuty)

  onAccent:     '#ffffff',   // Text/icon on colored buttons

  // ───── Severity ─────
  sevCritical:  '#EF4444',
  sevHigh:      '#F97316',
  sevMedium:    '#F59E0B',
  sevLow:       '#64748B',

  // ───── Status ─────
  statusActive:       '#10B981',
  statusDisconnected: '#F59E0B',
  statusInactive:     '#6B7280',

  // ───── Surfaces / overlays (alpha tokens) ─────
  surfaceHover:  'rgba(255,255,255,0.04)',   // row/button hovers
  surfaceSubtle: 'rgba(255,255,255,0.02)',   // card washes, quiet fills
  surfaceFaint:  'rgba(255,255,255,0.015)',  // zebra rows, faintest fills
  scrollThumb:      'rgba(255,255,255,0.12)',
  scrollThumbHover: 'rgba(255,255,255,0.22)',
  backdrop:      'rgba(0,0,0,0.5)',          // modal / palette overlays
  backdropHeavy: 'rgba(0,0,0,0.75)',

  inputBg:      '#0D1520',   // Composer / form input surface

  // ───── Glow color strings (bare rgba color values for box-shadows) ─────
  primaryGlow:  'rgba(59, 130, 246, 0.5)',
  accentGlow:   'rgba(226, 232, 240, 0.3)',
  successGlow:  'rgba(16, 185, 129, 0.5)',
  dangerGlow:   'rgba(239, 68, 68, 0.5)',
  warningGlow:  'rgba(245, 158, 11, 0.5)',

  // ───── Pallas-specific shadows ─────
  panelShadow:  '-8px 0 32px rgba(0, 0, 0, 0.6)',
  backdropBg:   'rgba(0, 0, 0, 0.5)',
};

// ─── Light palette (warm ivory — Anthropic-style) ────────────────────────────

const LIGHT_THEME = {
  // ───── Backgrounds (Claude-style warm ivory) ─────
  bg:           '#FAF9F5',   // Ivory page — matches claude.ai light
  bgDeep:       '#F0EEE6',   // Recessed canvas (trace log, code contrast)
  card:         '#FFFFFF',   // Elevated surface
  cardDark:     '#F1EFE9',   // Flat sections / tab containers
  cardSubtle:   '#FCFBF8',   // Meta bars, table headers
  cardHover:    '#F2F0EA',
  railBg:       '#FAF9F5',   // Sidebar matches the page in light (Claude-style)

  rowBg:        'rgba(0,0,0,0.02)',
  rowBgHover:   'rgba(0,0,0,0.045)',
  rowBgMuted:   'rgba(0,0,0,0.012)',

  // ───── Borders ─────
  border:       '#E0DCD1',
  borderLight:  '#C9C4B6',
  borderSubtle: '#EBE8DF',
  borderFaint:  '#F0EEE6',

  // ───── Text (warm gray ramp) ─────
  // Contrast on the #FAF9F5 page (measured): text 15.8:1, secondary 7.2:1,
  // muted 5.5:1, faint 4.2:1. The muted/faint steps were darkened from the
  // first pass (4.0:1 / 2.3:1) — small uppercase kickers were washing out.
  text:         '#1F1E1B',
  textBright:   '#121110',
  textSecondary:'#57544B',
  textMuted:    '#6B6759',
  textFaint:    '#78735F',
  textDisabled: '#B4AF9E',

  // ───── Brand Accent Colors ─────
  primary:      '#2563EB',   // Darker blue for light-surface contrast
  primaryDark:  '#1D4ED8',
  primaryLight: '#3B82F6',

  accent:       '#334155',   // Slate — silver is invisible on ivory
  accentDark:   '#1E293B',
  accentContrast: '#FFFFFF', // Text/icon ON the accent color

  success:      '#047857',
  successDark:  '#065F46',
  successLight: '#0D9488',
  successSoft:  '#047857',

  danger:       '#DC2626',
  dangerDark:   '#B91C1C',
  dangerSoft:   '#DC2626',

  warning:      '#B45309',
  warningDark:  '#92400E',
  warningYellow:'#A16207',
  warningSoft:  '#C2410C',

  info:         '#0E7490',
  violet:       '#7C3AED',

  onAccent:     '#FFFFFF',

  // ───── Severity ─────
  sevCritical:  '#DC2626',
  sevHigh:      '#EA580C',
  sevMedium:    '#B45309',
  sevLow:       '#64748B',

  // ───── Status ─────
  statusActive:       '#047857',
  statusDisconnected: '#B45309',
  statusInactive:     '#6B7280',

  // ───── Surfaces / overlays (alpha tokens) ─────
  surfaceHover:  'rgba(0,0,0,0.04)',
  surfaceSubtle: 'rgba(0,0,0,0.025)',
  surfaceFaint:  'rgba(0,0,0,0.015)',
  scrollThumb:      'rgba(0,0,0,0.20)',
  scrollThumbHover: 'rgba(0,0,0,0.32)',
  backdrop:      'rgba(35,32,26,0.4)',
  backdropHeavy: 'rgba(35,32,26,0.55)',

  inputBg:      '#FFFFFF',

  // ───── Glow color strings ─────
  primaryGlow:  'rgba(37, 99, 235, 0.25)',
  accentGlow:   'rgba(51, 65, 85, 0.2)',
  successGlow:  'rgba(4, 120, 87, 0.25)',
  dangerGlow:   'rgba(220, 38, 38, 0.25)',
  warningGlow:  'rgba(180, 83, 9, 0.25)',

  // ───── Pallas-specific shadows ─────
  panelShadow:  '-8px 0 32px rgba(35, 32, 26, 0.18)',
  backdropBg:   'rgba(35, 32, 26, 0.4)',
};

// ─── The mutable singleton ───────────────────────────────────────────────────
// Defaults to dark. index.js calls applyTheme() before first render; the
// sidebar toggle persists the choice and reloads.

export const THEME = { ...DARK_THEME };

export function applyTheme(mode) {
  Object.assign(THEME, mode === 'light' ? LIGHT_THEME : DARK_THEME);
}

// Resolve the saved theme mode ('dark' | 'light'). Safe in non-browser
// contexts; anything unrecognized falls back to dark.
export function getSavedThemeMode() {
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
    return saved === 'light' ? 'light' : 'dark';
  } catch (_) {
    return 'dark';
  }
}

// ─── Gradients ───────────────────────────────────────────────────────────────
// No live importers today (kept for compatibility). Values are dark-tuned.

export const GRADIENTS = {
  primary:           'linear-gradient(135deg, #3b82f6, #2563eb)',
  primaryHorizontal: 'linear-gradient(90deg, #3b82f6, #2563eb)',
  primaryAccent:     'linear-gradient(135deg, #3b82f6, #60a5fa)',
  accent:            'linear-gradient(135deg, #e2e8f0, #cbd5e1)',
  success:           'linear-gradient(135deg, #10b981, #059669)',
  warning:           'linear-gradient(135deg, #f59e0b, #d97706)',
  danger:            'linear-gradient(135deg, #ef4444, #dc2626)',

  card:              'linear-gradient(145deg, rgba(18, 18, 22, 0.96), rgba(18, 18, 22, 0.88))',
  bgFull:            'linear-gradient(135deg, #151519 0%, #1D1E24 50%, #151519 100%)',

  cveBadge:          'linear-gradient(135deg, rgba(245, 167, 0, 0.3), rgba(245, 167, 0, 0.1))',
  titleText:         'linear-gradient(135deg, #f8fafc, #cbd5e1)',

  sevCritical:       'linear-gradient(135deg, #ef4444, #dc2626)',
  sevHigh:           'linear-gradient(135deg, #f97316, #ea580c)',
  sevMedium:         'linear-gradient(135deg, #f59e0b, #d97706)',
  sevLow:            'linear-gradient(135deg, #64748b, #475569)',
};

// ─── Shadows ─────────────────────────────────────────────────────────────────
// Black-based; read acceptably on both themes (lighter feel comes from the
// surfaces, not the shadows). No live importers today.

export const SHADOWS = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.4)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)',
};

// ─── Glow box-shadows (retired to transparent in v2.5) ───────────────────────

export const GLOWS = {
  primary: '0 0 0 0 transparent',
  success: '0 0 0 0 transparent',
  danger:  '0 0 0 0 transparent',
  warning: '0 0 0 0 transparent',
  accent:  '0 0 0 0 transparent',
};

// ─── Animation Timings ───────────────────────────────────────────────────────

export const TIMINGS = {
  fast:    '0.15s',
  base:    '0.3s',
  slow:    '0.5s',
  slowest: '0.8s',
  ease:    'cubic-bezier(0.4, 0, 0.2, 1)',
};

// ─── Legacy alias ────────────────────────────────────────────────────────────
// DEPRECATED: no importers. Snapshots THEME at module-eval time (before any
// applyTheme/ageleia mutation) — do NOT import; read THEME directly instead.

export const AGELIA_COLORS = {
  background:    THEME.bg,
  panel:         THEME.card,
  panelDark:     THEME.cardDark,
  border:        THEME.border,
  borderLight:   THEME.borderLight,
  text:          THEME.text,
  textSecondary: THEME.textSecondary,
  textMuted:     THEME.textMuted,
  primary:       THEME.primary,
  success:       THEME.success,
  successLight:  THEME.successLight,
  danger:        THEME.danger,
  warning:       THEME.warning,
  warningYellow: THEME.warningYellow,
  accent:        THEME.accent,
  accentGlow:    THEME.accentGlow,
  dangerGlow:    THEME.dangerGlow,
  warningGlow:   THEME.warningGlow,
  successGlow:   THEME.successGlow,
  primaryGlow:   THEME.primaryGlow,
};
